import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { fromJS, List, Map } from 'immutable';
import { bindActionCreators } from 'redux';
import { debounce, isEmpty } from 'underscore';
import moment from 'moment';

import { downloadBrowseFile } from 'src/utils/downloadFile';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { BrowseCheckbox } from '../../components/Browse/BrowseCheckbox';
import { BrowseControlContent } from '../../components/Browse/BrowseControlContent';
import { BrowseInputRange } from '../../components/Browse/BrowseInputRange';
import BrowseInputTag from '../../components/Browse/BrowseInputTag';
import { BrowseAutoCompleteTag } from '../../components/Browse/BrowseAutoCompleteTag';
import { BrowseActionFilter } from '../../components/Browse/BrowseActionFilter';
import BrowseAutoCompleteCountry from '../../components/Browse/BrowseCountryFilter';
import config from '../../config';
import * as browseActionCreators from '../../actions/browse';
import {
  buildRangeParam,
  buildDateParam,
  buildTagParam,
  buildActionParam,
  buildCheckboxArrayParam,
  buildCheckboxObjectParam,
  buildSortByParam,
  buildDateRangeParam,
  buildBuyerActivityParam,
  buildActivityParam,
  buildTagObjectParam,
} from '../../helpers/paramBuilder';
import BrowseTable from '../../components/Browse/BrowseTable';
import { fetchBuyerStatuses } from '../../actions/statuses';
import { fetchEventActivities } from '../../actions/eventActivities';
import { BrowseStatusesFilter } from '../../components/Browse/BrowseStatusesFilter';
import { BrowseDateFilter } from '../../components/Browse/BrowseDateFilter';
import { BrowseBuyerActivityFilter } from '../../components/Browse/BrowseBuyerActivityFilter';
import { showError } from '../../utils/MessagePopup';
import { checkBrowsePageAccess } from '../../utils/checkPermissions';
import { BrowseActivityFilter } from '../../components/Browse/BrowseActivityFilter';
import { usePopupContext } from '../../contexts/PopupContext';
import { Card } from '../../components/helpers/Card';

import style from './style.module.css';

const SAFE_EXPORT_BUYERS_NUMBER = 300;

const companyTypes = config.values.get('companyTypes');
const states = fromJS(config.company.states.map(st => ({ name: st, value: st })));
const buyersFilter = config.browse.getIn(['filter', 'buyers']);
const columnDefs = config.browse.getIn(['table', 'columnDefs', 'buyers']);
const contextActionList = config.browse.getIn(['contextActionList', 'buyers']);

/**
 * Prepare moment() date object into ISO string.
 *
 * @param data Action data.
 */
const prepareActionData = data => ({
  ...data,
  endDate: moment.isMoment(data.endDate) ? data.endDate.toISOString() : data.endDate,
  startDate: moment.isMoment(data.startDate) ? data.startDate.toISOString() : data.startDate,
});

/**
 * Prepare moment() date object into ISO string.
 *
 * @param data Date data.
 */
const prepareDateData = data => ({
  ...data,
  min: moment.isMoment(data.min) ? data.min.toISOString() : data.min,
  max: moment.isMoment(data.max) ? data.max.toISOString() : data.max,
});

/**
 * Build all state data to restore it on a getting client.
 *
 * @param {object} filterState Buyers filter state.
 */
const buildFiltersStateParam = filterState => {
  const stateParams = {};

  // Collect needed filters if it's selected.
  if (filterState.getIn(['buyerType', 'checked'])) stateParams.buyerType = filterState.get('buyerType').toJS();
  if (filterState.getIn(['priority', 'checked'])) stateParams.priority = filterState.get('priority').toJS();
  if (filterState.getIn(['currentStatus', 'checked']))
    stateParams.currentStatus = filterState.get('currentStatus').toJS();
  if (filterState.getIn(['highStatus', 'checked'])) stateParams.highStatus = filterState.get('highStatus').toJS();
  if (filterState.getIn(['activity', 'checked'])) stateParams.activity = filterState.get('activity').toJS();
  if (filterState.getIn(['projectActivity', 'checked']))
    stateParams.projectActivity = filterState.get('projectActivity').toJS();
  if (filterState.getIn(['revenue', 'checked'])) stateParams.revenue = filterState.get('revenue').toJS();
  if (filterState.getIn(['ebitda', 'checked'])) stateParams.ebitda = filterState.get('ebitda').toJS();
  if (filterState.getIn(['ev', 'checked'])) stateParams.ev = filterState.get('ev').toJS();
  if (filterState.getIn(['transactionTypes', 'checked']))
    stateParams.transactionTypes = filterState.get('transactionTypes').toJS();
  if (filterState.getIn(['geography', 'checked'])) stateParams.geography = filterState.get('geography').toJS();
  if (filterState.getIn(['industry', 'checked'])) stateParams.industry = filterState.get('industry').toJS();
  if (filterState.getIn(['harvcoContact', 'checked']))
    stateParams.harvcoContact = filterState.get('harvcoContact').toJS();
  if (filterState.getIn(['companyType', 'checked'])) stateParams.companyType = filterState.get('companyType').toJS();
  if (filterState.getIn(['companyState', 'checked'])) stateParams.companyState = filterState.get('companyState').toJS();
  if (filterState.getIn(['areaCode', 'checked'])) stateParams.areaCode = filterState.get('areaCode').toJS();
  if (filterState.getIn(['nextAction', 'checked'])) {
    stateParams.nextAction = prepareActionData(filterState.get('nextAction').toJS());
  }

  if (filterState.getIn(['mergeResultOnly', 'checked'])) {
    stateParams.mergeResultOnly = prepareActionData(filterState.get('mergeResultOnly').toJS());
  }

  if (filterState.getIn(['activity', 'checked'])) {
    stateParams.activity = prepareActionData(filterState.get('activity').toJS());
  }
  if (filterState.getIn(['harvcoTags', 'checked'])) stateParams.harvcoTags = filterState.get('harvcoTags').toJS();
  if (filterState.getIn(['country', 'checked'])) stateParams.country = filterState.get('country').toJS();
  if (filterState.getIn(['startDate', 'checked'])) {
    stateParams.startDate = prepareDateData(filterState.get('startDate').toJS());
  }

  if (filterState.getIn(['endDate', 'checked'])) {
    stateParams.endDate = prepareDateData(filterState.get('endDate').toJS());
  }

  if (filterState.getIn(['keywords', 'checked'])) {
    stateParams.keywords = filterState.get('keywords').toJS();
  }

  return stateParams;
};

const buildRequestParam = filterState => {
  const requestParams = {
    ...buildCheckboxArrayParam(filterState, 'buyer_type', 'buyerType', 'paramName'),
    ...buildTagParam(filterState, 'priorities', 'priority', 'value'),
    ...buildRangeParam(filterState, 'current_status_range', 'currentStatus'),
    ...buildRangeParam(filterState, 'high_status_range', 'highStatus'),
    ...buildBuyerActivityParam(filterState, 'activity_search', 'projectActivity'),
    ...buildRangeParam(filterState, 'revenue_range', 'revenue'),
    ...buildRangeParam(filterState, 'ebitda_range', 'ebitda'),
    ...buildRangeParam(filterState, 'ev_range', 'ev'),
    ...buildCheckboxObjectParam(filterState, 'transaction_types', 'transactionTypes'),
    ...buildTagParam(filterState, 'geography', 'geography', 'value'),
    ...buildTagParam(filterState, 'industries', 'industry', 'value'),
    ...buildTagParam(filterState, 'contacts', 'harvcoContact', 'id'),
    ...buildTagParam(filterState, 'company_types', 'companyType', 'value'),
    ...buildTagParam(filterState, 'states', 'companyState', 'value'),
    ...buildTagParam(filterState, 'area_codes', 'areaCode', 'value'),
    ...buildDateParam(filterState, 'next_action_dates', 'nextAction'),
    ...buildActionParam(filterState, 'next_actions', 'nextAction'),
    ...buildTagParam(filterState, 'tags', 'harvcoTags', 'value'),
    ...buildTagParam(filterState, 'countries', 'country', 'id'),
    ...buildTagParam(filterState, 'continents', 'continent', 'id'),
    ...buildTagParam(filterState, 'keywords', 'keywords', 'value'),
    date_range: {
      ...buildDateRangeParam(filterState, 'start_date', 'startDate'),
      ...buildDateRangeParam(filterState, 'end_date', 'endDate'),
    },
  };

  if (filterState.getIn(['keywords', 'includeSubsidiaries'])) {
    if (requestParams.keywords) {
      requestParams.keywords.include_subsidiaries = filterState.getIn(['keywords', 'includeSubsidiaries']);
    }
  }

  if (filterState.getIn(['mergeResultOnly', 'checked'])) {
    requestParams.merge_result_only = filterState.getIn(['mergeResultOnly', 'checked']);
  }

  if (filterState.getIn(['activity', 'lastActivity'])) {
    Object.assign(requestParams, buildDateParam(filterState, 'last_action_dates', 'activity'));
    Object.assign(requestParams, buildTagObjectParam(filterState, 'last_actions', 'activity'));
  } else {
    Object.assign(requestParams, buildActivityParam(filterState, 'activity', 'activity'));
  }

  if (Array.isArray(requestParams.activity)) {
    const activity = buildTagParam(filterState, 'users', 'activity.users', 'id');
    const eventActivities = buildTagObjectParam(filterState, 'activities', 'activity', 'id');

    if (activity) {
      requestParams.activity.push({
        users: activity.users,
      });
    }

    if (eventActivities) {
      requestParams.activity.forEach(activityItem => {
        if (activityItem.activities) {
          // eslint-disable-next-line no-param-reassign
          activityItem.activities = eventActivities.activities;
        }
      });
    }
  }

  if (filterState.getIn(['activity', 'buyerType', 'checked'])) {
    if (Array.isArray(requestParams.activity)) {
      const { activity } = requestParams;

      activity.push({
        buyer_type: filterState.getIn(['activity', 'buyerType', 'selected', 'value']),
      });
      Object.assign(requestParams, { activity });
    }
  }

  if (filterState.getIn(['activity', 'statusChanged'])) {
    if (Array.isArray(requestParams.activity)) {
      const statusChangedFromValues = filterState.getIn(['activity', 'statusChangedFrom', 'text']).toJS();
      const statusChangedToValues = filterState.getIn(['activity', 'statusChangedTo', 'text']).toJS();
      const activity = requestParams.activity.slice();

      activity.push({
        status_changed_from: {
          min: statusChangedFromValues.min,
          max: statusChangedFromValues.max,
        },
      });
      activity.push({
        status_changed_to: {
          min: statusChangedToValues.min,
          max: statusChangedToValues.max,
        },
      });
      Object.assign(requestParams, { activity });
    }
  }

  return requestParams;
};

export const BrowseBuyers = props => {
  const {
    activities,
    allActivitiesWithoutDuplicates,
    buyers,
    handleAbortRequest,
    handleBrowse,
    handleClearFilter,
    handleConfirmMerging,
    handleExportWithSearchCriteria,
    handleFetchCriteria,
    handleGetBuyerSuggestion,
    handleGetHarvCoLeadsSuggestion,
    handleGetUsersSuggestion,
    handleSaveAddUpdateBuyer,
    handleSearchParamsBeforeBrowse,
    handleTargetDelete,
    handleTargetPushNextActionsDates,
    handleTargetSetNextActions,
    handleTargetsAddTag,
    handleUpdateBuyersActivityFilter,
    handleUpdateFilter,
    handleUpdateQueryResult,
    location,
    massSelectRows,
    onFetchEventActivities,
    onFetchStatuses,
    setSearchParamsHash,
    statuses,
    targets,
    updateTabVisibility,
    user,
    ...rest
  } = props;

  const { openPopup, closePopup } = usePopupContext();

  const keepSelectionRef = useRef(false);
  const hasMountedRef = useRef(false);
  const previousSortModelRef = useRef(buyers.getIn(['sortBy', 'sortModel']));
  // Bundles every prop a debounced handler reads live (rather than one ref per value), per
  // [[feedback-minimize-useref]] - debounced handlers can't use the re-subscription trick since
  // that would reset their debounce timers, so a ref is genuinely unavoidable here.
  const liveRef = useRef({ buyers, activities, targets, allActivitiesWithoutDuplicates });

  liveRef.current = { buyers, activities, targets, allActivitiesWithoutDuplicates };

  const hasPageAccess = checkBrowsePageAccess(user, 'buyers');

  const handleChange = useCallback(
    data => {
      if (data.filterName === 'sortBy') {
        keepSelectionRef.current = true;
      }

      const extra = data.key ? { key: data.key } : {};

      handleUpdateFilter({
        ...extra,
        name: 'buyers',
        filterName: data.filterName,
        filterData: data.filterData,
      });
    },
    [handleUpdateFilter],
  );

  const handleSubActivityFilterChange = useCallback(
    data => {
      handleUpdateBuyersActivityFilter(data);
    },
    [handleUpdateBuyersActivityFilter],
  );

  const handleGetNextPageData = useCallback(
    (page, keepSelection = true) => {
      const params = buildRequestParam(buyers);
      const sortBy = buildSortByParam(buyers);

      handleSearchParamsBeforeBrowse({
        params: {
          requestParams: params,
          stateParams: buildFiltersStateParam(buyers),
        },
        name: 'buyers',
        afterSuccess: () => {
          handleBrowse({
            name: 'buyers',
            params,
            page,
            sortBy,
            keepSelection,
          });
        },
      });
    },
    [buyers, handleSearchParamsBeforeBrowse, handleBrowse],
  );

  const fetchSuggestionsForStates = useCallback(
    ({ value }) => {
      const suggestions = states.filter(
        state =>
          state
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'buyers',
        filterName: 'companyState',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForStates = useMemo(() => debounce(fetchSuggestionsForStates, 200), [
    fetchSuggestionsForStates,
  ]);

  const fetchSuggestionsForActivityUser = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('buyers.activity', 'users', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForActivityUser = useMemo(() => debounce(fetchSuggestionsForActivityUser, 200), [
    fetchSuggestionsForActivityUser,
  ]);

  const fetchSuggestionsForActivityBuyer = useCallback(
    ({ value }) => {
      handleGetBuyerSuggestion('buyers.activity', 'buyers', value, 1, true);
    },
    [handleGetBuyerSuggestion],
  );
  const handleGetSuggestionForActivityBuyer = useMemo(() => debounce(fetchSuggestionsForActivityBuyer, 200), [
    fetchSuggestionsForActivityBuyer,
  ]);

  const fetchNextSuggestionsForActivityUser = useCallback(
    ({ value }) => {
      const info = liveRef.current.buyers.getIn(['activity', 'users', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('buyers.activity', 'users', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForActivityUser = useMemo(
    () => debounce(fetchNextSuggestionsForActivityUser, 200),
    [fetchNextSuggestionsForActivityUser],
  );

  const fetchNextSuggestionsForActivityBuyer = useCallback(
    ({ value }) => {
      // Pre-existing bug (also present in BrowseExecutives.js): reads `targets`, which this
      // component never receives as a prop, so `info` is always undefined here. Preserved as-is.
      const info = liveRef.current.targets.getIn(['activity', 'buyers', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetBuyerSuggestion('buyers.activity', 'buyers', value, currentPage + 1, true);
      }
    },
    [handleGetBuyerSuggestion],
  );
  const handleGetNextSuggestionForActivityBuyer = useMemo(
    () => debounce(fetchNextSuggestionsForActivityBuyer, 200),
    [fetchNextSuggestionsForActivityBuyer],
  );

  const fetchSuggestionsForHarvcoContact = useCallback(
    ({ value }) => {
      handleGetHarvCoLeadsSuggestion('buyers', value);
    },
    [handleGetHarvCoLeadsSuggestion],
  );
  const handleGetSuggestionForHarvcoContact = useMemo(() => debounce(fetchSuggestionsForHarvcoContact, 200), [
    fetchSuggestionsForHarvcoContact,
  ]);

  const fetchNextSuggestionsForHarvcoContact = useCallback(
    ({ value }) => {
      const info = liveRef.current.buyers.getIn(['harvcoContact', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetHarvCoLeadsSuggestion('buyers', value, currentPage + 1);
      }
    },
    [handleGetHarvCoLeadsSuggestion],
  );
  const handleGetNextSuggestionForHarvcoContact = useMemo(
    () => debounce(fetchNextSuggestionsForHarvcoContact, 200),
    [fetchNextSuggestionsForHarvcoContact],
  );

  const fetchSuggestionsForNextAction = useCallback(
    ({ value }) => {
      const suggestions = liveRef.current.allActivitiesWithoutDuplicates.filter(
        activity =>
          activity
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'buyers',
        filterName: 'nextAction',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForNextAction = useMemo(() => debounce(fetchSuggestionsForNextAction, 200), [
    fetchSuggestionsForNextAction,
  ]);

  const fetchSuggestionsForActivity = useCallback(
    ({ value }) => {
      const suggestions = (liveRef.current.activities ?? List()).filter(
        activity =>
          activity
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'buyers',
        filterName: 'activity',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForActivity = useMemo(() => debounce(fetchSuggestionsForActivity, 200), [
    fetchSuggestionsForActivity,
  ]);

  const fetchSuggestionsForCompanyType = useCallback(
    ({ value }) => {
      const suggestions = companyTypes.filter(
        companyType =>
          companyType
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'buyers',
        filterName: 'companyType',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForCompanyType = useMemo(() => debounce(fetchSuggestionsForCompanyType, 200), [
    fetchSuggestionsForCompanyType,
  ]);

  const handleBuyerTypeSubCheckboxChange = useCallback(
    data => {
      handleUpdateFilter({
        name: 'buyers',
        filterName: 'buyerType',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [handleUpdateFilter],
  );

  const handleBuyerActivityChange = useCallback(
    data => {
      handleUpdateFilter({
        name: 'buyers',
        filterName: 'projectActivity',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [handleUpdateFilter],
  );

  const handleTransactionTypeSubCheckboxChange = useCallback(
    data => {
      handleUpdateFilter({
        name: 'buyers',
        filterName: 'transactionTypes',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [handleUpdateFilter],
  );

  const fetchSuggestionsForPriority = useCallback(
    ({ value }) => {
      const suggestions = [
        { value: 'A', name: 'A' },
        { value: 'B', name: 'B' },
        { value: 'C', name: 'C' },
      ];

      if (value === '') {
        handleUpdateFilter({
          name: 'buyers',
          filterName: 'priority',
          filterData: {
            suggestions,
          },
        });
      } else {
        handleUpdateFilter({
          name: 'buyers',
          filterName: 'priority',
          filterData: {
            suggestions: [],
          },
        });
      }
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForPriority = useMemo(() => debounce(fetchSuggestionsForPriority, 200), [
    fetchSuggestionsForPriority,
  ]);

  const handleGetCountriesByContinent = useCallback(
    (value, event) => {
      const { checked } = event.currentTarget;
      const country = buyers.get('country');
      const allCountriesSuggestions = buyers.get('allCountriesSuggestions');
      const existingSuggestions = country.get('selectedList');

      let newSuggestions;

      if (checked) {
        const checkedCountries = allCountriesSuggestions
          .filter(suggestion => suggestion.continentId === value)
          .map(suggestion =>
            Map({
              include: true,
              id: suggestion.id,
              text: suggestion.text,
              continentId: value,
            }),
          );

        newSuggestions = existingSuggestions.push(...checkedCountries);
      } else {
        newSuggestions = country.get('selectedList').filter(suggestion => suggestion.get('continentId') !== value);
      }

      handleChange({
        filterName: 'country',
        filterData: {
          selectedList: newSuggestions,
        },
      });
    },
    [buyers, handleChange],
  );

  const handleDoubleClickRow = useCallback(row => {
    window.open(`/company/${row.data.id}`, '_blank');
  }, []);

  const handleToggle = useCallback(() => {
    updateTabVisibility({
      field: 'buyers.showTabControls',
      value: !buyers.get('showTabControls'),
    });
  }, [buyers, updateTabVisibility]);

  const handleFilterClear = useCallback(
    name => {
      handleClearFilter({ name });
    },
    [handleClearFilter],
  );

  const handleDownloadExport = useCallback(
    (idList, params) => {
      const url = `${config.API_BASE_URL}/api/v1/browse/buyers/export`;

      if (idList.length > SAFE_EXPORT_BUYERS_NUMBER) {
        return handleExportWithSearchCriteria(url, idList, params);
      }

      downloadBrowseFile(url, {
        selected_ids: idList,
        hash: params.hash,
      });
    },
    [handleExportWithSearchCriteria],
  );

  const handleRequestAbort = useCallback(
    name => {
      handleAbortRequest(name);
    },
    [handleAbortRequest],
  );

  useEffect(() => {
    onFetchStatuses();
    onFetchEventActivities();

    const searchParams = new URLSearchParams(location.search);
    const criteria = searchParams.get('criteria');

    if (criteria) {
      const payload = {
        name: 'buyers',
        hash: criteria,
      };

      handleFetchCriteria(payload);
      setSearchParamsHash(payload);
    }

    if (!hasPageAccess) {
      showError(openPopup, config.permisionError, () => {
        closePopup();
      });
    }
    // Mount-only: matches the original componentDidMount exactly.
  }, []);

  useEffect(() => {
    const newSortModel = buyers.getIn(['sortBy', 'sortModel']);

    if (hasMountedRef.current) {
      if (newSortModel !== previousSortModelRef.current) {
        const requestParam = buildRequestParam(buyers);

        if (!isEmpty(requestParam)) {
          const sortBy = buildSortByParam(buyers);

          handleBrowse({
            name: 'buyers',
            params: requestParam,
            page: 1,
            sortBy,
            keepSelection: keepSelectionRef.current,
          });
          keepSelectionRef.current = false;
        }
      }
    } else {
      hasMountedRef.current = true;
    }

    previousSortModelRef.current = newSortModel;
  }, [buyers, handleBrowse]);

  const controlClassName = classNames(style.controls, { [style.controlsHidden]: !buyers.get('showTabControls') });

  const params = {
    requestParams: buildRequestParam(buyers),
    stateParams: buildFiltersStateParam(buyers),
  };

  const hasParams = !isEmpty(params.requestParams);

  return (
    <div className="tab-pane active" id="browse-buyers" role="tabpanel">
      {hasPageAccess && (
        <div className={style.inner}>
          <BrowseTable
            columnDefs={columnDefs}
            contextActionList={contextActionList}
            data={buyers}
            hasParams={hasParams}
            location={location}
            name="buyers"
            onAbortRequest={handleRequestAbort}
            onAddTag={handleTargetsAddTag}
            onChange={handleChange}
            onClearFilter={handleFilterClear}
            onConfirmMerging={handleConfirmMerging}
            onDelete={handleTargetDelete}
            onDoubleClickRow={handleDoubleClickRow}
            onDownloadExport={handleDownloadExport}
            onGetNextPageData={handleGetNextPageData}
            onGetPageData={handleGetNextPageData}
            onPushNextActionsDates={handleTargetPushNextActionsDates}
            onSaveAddUpdateBuyer={handleSaveAddUpdateBuyer}
            onSearchParamsBeforeBrowse={handleSearchParamsBeforeBrowse}
            onSelectRows={massSelectRows}
            onSetNextActions={handleTargetSetNextActions}
            onToggle={handleToggle}
            onUpdateQueryResult={handleUpdateQueryResult}
            requestParams={params.requestParams}
            stateParams={params.stateParams}
            user={user}
            {...rest}
          />
          <Card className={controlClassName} title="Filters">
            <BrowseCheckbox
              filter={buyersFilter.get('buyerType')}
              info={buyers.get('buyerType')}
              onChange={handleChange}
            >
              <BrowseControlContent variant="checkbox">
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['buyerType', 'financial'])}
                  info={buyers.getIn(['buyerType', 'financial'])}
                  onChange={handleBuyerTypeSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['buyerType', 'strategic'])}
                  info={buyers.getIn(['buyerType', 'strategic'])}
                  onChange={handleBuyerTypeSubCheckboxChange}
                />
              </BrowseControlContent>
            </BrowseCheckbox>
            <BrowseAutoCompleteTag
              filter={buyersFilter.get('priority')}
              info={buyers.get('priority')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForPriority}
            />
            <BrowseStatusesFilter
              filter={buyersFilter.get('currentStatus')}
              info={buyers.get('currentStatus')}
              onChange={handleChange}
              statuses={statuses}
            />
            <BrowseStatusesFilter
              filter={buyersFilter.get('highStatus')}
              info={buyers.get('highStatus')}
              onChange={handleChange}
              statuses={statuses}
            />
            <BrowseCheckbox
              filter={buyersFilter.get('projectActivity')}
              info={buyers.get('projectActivity')}
              onChange={handleChange}
            >
              <BrowseControlContent variant="checkbox">
                <BrowseBuyerActivityFilter
                  filter={buyersFilter.getIn(['projectActivity', 'contacted'])}
                  info={buyers.getIn(['projectActivity', 'contacted'])}
                  onChange={handleBuyerActivityChange}
                />
                <BrowseBuyerActivityFilter
                  filter={buyersFilter.getIn(['projectActivity', 'currentLead'])}
                  info={buyers.getIn(['projectActivity', 'currentLead'])}
                  onChange={handleBuyerActivityChange}
                />
                <BrowseBuyerActivityFilter
                  filter={buyersFilter.getIn(['projectActivity', 'totalLead'])}
                  info={buyers.getIn(['projectActivity', 'totalLead'])}
                  onChange={handleBuyerActivityChange}
                />
                <BrowseBuyerActivityFilter
                  filter={buyersFilter.getIn(['projectActivity', 'cc'])}
                  info={buyers.getIn(['projectActivity', 'cc'])}
                  onChange={handleBuyerActivityChange}
                />
                <BrowseBuyerActivityFilter
                  filter={buyersFilter.getIn(['projectActivity', 'visit'])}
                  info={buyers.getIn(['projectActivity', 'visit'])}
                  onChange={handleBuyerActivityChange}
                />
                <BrowseBuyerActivityFilter
                  filter={buyersFilter.getIn(['projectActivity', 'offer'])}
                  info={buyers.getIn(['projectActivity', 'offer'])}
                  onChange={handleBuyerActivityChange}
                />
              </BrowseControlContent>
            </BrowseCheckbox>
            <BrowseInputRange
              filter={buyersFilter.get('revenue')}
              info={buyers.get('revenue')}
              onChange={handleChange}
            />
            <BrowseInputRange filter={buyersFilter.get('ebitda')} info={buyers.get('ebitda')} onChange={handleChange} />
            <BrowseInputRange filter={buyersFilter.get('ev')} info={buyers.get('ev')} onChange={handleChange} />
            <BrowseCheckbox
              filter={buyersFilter.get('transactionTypes')}
              info={buyers.get('transactionTypes')}
              onChange={handleChange}
            >
              <BrowseControlContent variant="checkbox">
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['transactionTypes', 'buyout'])}
                  info={buyers.getIn(['transactionTypes', 'buyout'])}
                  onChange={handleTransactionTypeSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['transactionTypes', 'recap'])}
                  info={buyers.getIn(['transactionTypes', 'recap'])}
                  onChange={handleTransactionTypeSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['transactionTypes', 'mezz'])}
                  info={buyers.getIn(['transactionTypes', 'mezz'])}
                  onChange={handleTransactionTypeSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['transactionTypes', 'carveout'])}
                  info={buyers.getIn(['transactionTypes', 'carveout'])}
                  onChange={handleTransactionTypeSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['transactionTypes', 'takeprivate'])}
                  info={buyers.getIn(['transactionTypes', 'takeprivate'])}
                  onChange={handleTransactionTypeSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['transactionTypes', 'minority'])}
                  info={buyers.getIn(['transactionTypes', 'minority'])}
                  onChange={handleTransactionTypeSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={buyersFilter.getIn(['transactionTypes', 'turnaround'])}
                  info={buyers.getIn(['transactionTypes', 'turnaround'])}
                  onChange={handleTransactionTypeSubCheckboxChange}
                />
              </BrowseControlContent>
            </BrowseCheckbox>
            <BrowseInputTag
              filter={buyersFilter.get('geography')}
              info={buyers.get('geography')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseInputTag
              filter={buyersFilter.get('industry')}
              info={buyers.get('industry')}
              onChange={handleChange}
              isBIC
              submitOnEnter
            />
            <BrowseAutoCompleteTag
              filter={buyersFilter.get('harvcoContact')}
              info={buyers.get('harvcoContact')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForHarvcoContact}
              onGetSuggestion={handleGetSuggestionForHarvcoContact}
            />
            <BrowseAutoCompleteTag
              filter={buyersFilter.get('companyType')}
              info={buyers.get('companyType')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForCompanyType}
            />
            <BrowseAutoCompleteTag
              filter={buyersFilter.get('companyState')}
              info={buyers.get('companyState')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForStates}
            />
            <BrowseInputTag
              filter={buyersFilter.get('areaCode')}
              info={buyers.get('areaCode')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseActionFilter
              filter={buyersFilter.get('nextAction')}
              info={buyers.get('nextAction')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForNextAction}
            />
            <BrowseActivityFilter
              buyersFilter={buyersFilter.getIn(['activity', 'buyers'])}
              buyersInfo={buyers.getIn(['activity', 'buyers'])}
              filter={buyersFilter.get('activity')}
              info={buyers.get('activity')}
              onChange={handleChange}
              onGetActivityUserSuggestion={handleGetSuggestionForActivityUser}
              onGetBuyerSuggestion={handleGetSuggestionForActivityBuyer}
              onGetNextActivityUserSuggestion={handleGetNextSuggestionForActivityUser}
              onGetNextBuyerSuggestion={handleGetNextSuggestionForActivityBuyer}
              onGetSuggestion={handleGetSuggestionForActivity}
              onSubActivityFilterChange={handleSubActivityFilterChange}
              projectTypeFilter={buyersFilter.getIn(['activity', 'buyerType'])}
              projectTypeInfo={buyers.getIn(['activity', 'buyerType'])}
              statuses={statuses}
              usersFilter={buyersFilter.getIn(['activity', 'users'])}
              usersInfo={buyers.getIn(['activity', 'users'])}
            />
            <BrowseInputTag
              filter={buyersFilter.get('harvcoTags')}
              info={buyers.get('harvcoTags')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseAutoCompleteCountry
              allCountriesSuggestions={buyers.get('allCountriesSuggestions')}
              continents={buyers.getIn(['continent', 'suggestions'])}
              filter={buyersFilter.get('country')}
              info={buyers.get('country')}
              onChange={handleChange}
              onGetCountriesByContinent={handleGetCountriesByContinent}
              type="buyers"
            />
            <BrowseDateFilter
              filter={buyersFilter.get('startDate')}
              info={buyers.get('startDate')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseDateFilter
              filter={buyersFilter.get('endDate')}
              info={buyers.get('endDate')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseInputTag
              filter={buyersFilter.get('keywords')}
              includeSubsidiaries={buyers.getIn(['keywords', 'includeSubsidiaries'])}
              info={buyers.get('keywords')}
              onChange={handleChange}
              shouldGroupTags
              submitOnEnter
            />
            <BrowseCheckbox
              filter={buyersFilter.get('mergeResultOnly')}
              info={buyers.get('mergeResultOnly')}
              onChange={handleChange}
            >
              <>&nbsp;</>
            </BrowseCheckbox>
          </Card>
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    buyers: state.browse.get('buyers'),
    statuses: state.statuses.get('buyerStatuses'),
    user: state.auth.get('user'),
    activities: state.eventActivities.getIn(['activities', 'all', 'buyer']),
    allActivitiesWithoutDuplicates: state.eventActivities.get('allActivitiesWithoutDuplicates'),
  };
}

const mapDispatchToProps = dispatch => ({
  onFetchStatuses: () => dispatch(fetchBuyerStatuses()),
  onFetchEventActivities: () => dispatch(fetchEventActivities()),
  ...bindActionCreators(browseActionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(BrowseBuyers));
